import { PAYMENT_ACCOUNT_STATUS } from '@/types/payment-account';

/**
 * Textos do assistente de ativação (plano, seção 6.3 — rota
 * `(provider)/payments/activate.tsx`, T4.3). Uma pergunta por tela quando
 * possível; nada aqui pré-marca resposta — isso é regra da tela (`calm-ui`),
 * não do texto.
 */

export const ACTIVATION_STEP_YOU = {
  title: 'Você',
  legalNameLabel: 'Nome como está no CPF',
  legalNameHelper: 'Confira: é o nome que vai ser verificado.',
  documentLabel: 'CPF',
  birthDateLabel: 'Data de nascimento',
  companyLinkLabel: 'Recebo como empresa (CNPJ)',
  companyDocumentLabel: 'CNPJ',
  companyNameLabel: 'Razão social',
};

export const ACTIVATION_STEP_ADDRESS = {
  title: 'Endereço',
  zipCodeLabel: 'CEP',
  zipCodeHelper: 'Preenchemos o resto para você.',
  zipCodeNotFound: 'Não encontramos esse CEP. Preencha o endereço à mão.',
  numberLabel: 'Número',
  complementLabel: 'Complemento',
  streetLabel: 'Rua',
  neighborhoodLabel: 'Bairro',
  cityLabel: 'Cidade',
  stateLabel: 'UF',
};

export const ACTIVATION_STEP_PEP = {
  title: 'Uma pergunta',
  question: 'Você ocupa ou ocupou cargo público relevante nos últimos 5 anos?',
  helper:
    'É a chamada "pessoa politicamente exposta" — cargos como ministro, deputado, prefeito, secretário ou diretor de estatal. Pergunta padrão de qualquer plataforma de pagamento, não muda nada na sua ativação.',
  yesLabel: 'Sim',
  noLabel: 'Não',
};

export const ACTIVATION_STEP_BANK = {
  title: 'Onde quer receber',
  bankLabel: 'Banco',
  bankSearchPlaceholder: 'Busque pelo nome ou código',
  agencyLabel: 'Agência',
  accountLabel: 'Conta',
  accountDigitLabel: 'Dígito',
  accountTypeLabel: 'Tipo de conta',
  accountTypeCheckingLabel: 'Corrente',
  accountTypeSavingsLabel: 'Poupança',
  notice: 'A conta precisa estar no seu CPF (ou no CNPJ, se você ativar como empresa).',
};

export const ACTIVATION_STEP_REVIEW = {
  title: 'Revisão',
  summaryTitle: 'Confira antes de continuar',
  termsLabel: 'Li e aceito os termos de recebimento da Controlapag',
  termsLinkLabel: 'termos de recebimento',
  continueButton: 'Continuar',
};

export const ACTIVATION_STEP_STRIPE = {
  title: 'Confirmação',
  intro: 'Agora a processadora de pagamentos confirma seus dados.',
  helper:
    'Você vai para a página segura da processadora: confirma seu e-mail e celular, revisa o que já preenchemos, aceita o contrato e envia foto do documento, se pedirem. No fim, volta sozinho para cá.',
  cta: 'Continuar cadastro',
};

export const ACTIVATION_STEP_STRIPE_CHECKING = {
  title: 'Conferindo seu cadastro',
  body: 'Só um instante enquanto confirmamos com a processadora.',
};

export const ACTIVATION_STEP_STRIPE_INCOMPLETE = {
  intro: 'Seu cadastro ainda não foi concluído.',
  helper:
    'Faltou alguma etapa na página da processadora — como aceitar o contrato ou enviar o documento. Continue de onde parou; o que você já preencheu está salvo.',
  cta: 'Continuar de onde parei',
};

export const ACTIVATION_STATE_VERIFYING = {
  title: 'Em análise',
  body: 'Normalmente leva só alguns minutos. Avisamos assim que estiver pronto — pode fechar esta tela e continuar usando o app.',
};

export const ACTIVATION_STATE_ACTIVE = {
  title: 'Pronto!',
  body: 'A partir de agora, suas novas cobranças são confirmadas sozinhas.',
  convertOpenPrompt: 'Passar cobranças em aberto para automático?',
  convertOpenButton: 'Passar para automático',
  skipButton: 'Deixar como está',
};

/**
 * `ACTIVE` com `paymentCollection: 'AUTOMATIC'` (T4.5, revisão pós-`0380a3a`):
 * o recebimento automático já está ligado, então a tela não oferece mais
 * "Passar para automático" — só confirma o estado e dá o atalho para
 * Recebimentos.
 */
export const ACTIVATION_STATE_ACTIVE_AUTOMATIC_ON = {
  body: 'O recebimento automático está ligado. Suas cobranças são confirmadas sozinhas.',
  receivablesButton: 'Ver Recebimentos',
};

export const ACTIVATION_STATE_ACTION_REQUIRED = {
  title: 'Falta um passo',
  body: 'Ainda falta alguma informação para liberar seu recebimento.',
  resumeButton: 'Resolver pendência',
  /** `requirementsDue.currentDeadline` formatado por quem monta a tela — este texto só dá o molde. */
  deadlinePrefix: 'Prazo:',
};

/**
 * Tela no lugar do formulário (T4.3) quando `prefill` vem vazio ou o
 * `POST /payment-account` devolve 403 — hoje, só quem é dono da conta em
 * caixa único pode ativar (`PAYMENT_ACCOUNT_OWNER_ONLY`/`PAYMENT_ACCOUNT_SELF_ONLY`,
 * `api/src/payment-account/payment-account.service.ts`). A frase real do
 * servidor aparece primeiro; este texto é só o molde de quando ela falhar.
 */
export const ACTIVATION_BLOCKED_FALLBACK = {
  title: 'Só o dono ativa',
  body: 'O recebimento automático é configurado pelo dono do negócio, em Configurações.',
};

/** Idem, para `PAYMENT_ACCOUNT_PLAN_INELIGIBLE` (400): plano sem direito ao recurso. */
export const ACTIVATION_PLAN_REQUIRED_FALLBACK = {
  title: 'Plano necessário',
  body: 'Esse recurso está disponível para quem tem um plano ativo ou está no período de teste.',
};

/**
 * `RESTRICTED`/`DISABLED` (T4.5): fora dos três estados nomeados no plano
 * ("Em análise" / "Pronto!" / "Pendência"), mas a conta pode assumi-los de
 * verdade (`api/src/payment-account/resolve-account-status.ts`). Fallback
 * mínimo para a tela nunca ficar em branco — tratamento completo, com o
 * componente **Aviso** do Stripe, é da tela Recebimentos (T6.1) e de
 * Configurar recebimento (T6.4), ainda não construídas.
 */
export const ACTIVATION_STATE_RESTRICTED = {
  title: 'Pagamentos parados',
  body: 'O provedor de pagamento encontrou um risco na sua conta e pausou os pagamentos e repasses por enquanto. Nossa equipe foi avisada.',
};

export const ACTIVATION_STATE_RESTRICTED_FIXABLE = {
  title: 'Faltam dados para liberar',
  body: 'A processadora não conseguiu confirmar alguns dados do cadastro, como o endereço. Corrija ou envie um comprovante para liberar os pagamentos.',
  cta: 'Corrigir dados',
};

export const ACTIVATION_STATE_DISABLED = {
  title: 'Desligado',
  body: 'O recebimento automático está desligado nesta conta.',
};

/**
 * Segunda linha do card do Perfil quando a conta pede ação de quem gerencia:
 * o rótulo diz o estado, isto diz que é tocando ali que se resolve.
 * `null` = nada a fazer (ou nada que o prestador possa fazer).
 */
export function getAccountStatusHint(
  status: PAYMENT_ACCOUNT_STATUS,
  hasRequirementsDue: boolean,
): string | null {
  switch (status) {
    case PAYMENT_ACCOUNT_STATUS.DRAFT:
      return 'Toque para concluir o cadastro';
    case PAYMENT_ACCOUNT_STATUS.ACTION_REQUIRED:
      return 'Toque para resolver a pendência';
    case PAYMENT_ACCOUNT_STATUS.RESTRICTED:
      return hasRequirementsDue
        ? 'Faltam dados. Toque para corrigir'
        : 'Toque para ver o que aconteceu';
    default:
      return null;
  }
}

/** Rótulo curto por status (card do Perfil, T4.5) — sempre o mesmo texto do título do estado cheio. */
export function getAccountStatusLabel(status: PAYMENT_ACCOUNT_STATUS): string {
  switch (status) {
    case PAYMENT_ACCOUNT_STATUS.DRAFT:
      return 'Cadastro incompleto';
    case PAYMENT_ACCOUNT_STATUS.VERIFYING:
      return ACTIVATION_STATE_VERIFYING.title;
    case PAYMENT_ACCOUNT_STATUS.ACTIVE:
      return ACTIVATION_STATE_ACTIVE.title;
    case PAYMENT_ACCOUNT_STATUS.ACTION_REQUIRED:
      return ACTIVATION_STATE_ACTION_REQUIRED.title;
    case PAYMENT_ACCOUNT_STATUS.RESTRICTED:
      return ACTIVATION_STATE_RESTRICTED.title;
    case PAYMENT_ACCOUNT_STATUS.DISABLED:
      return ACTIVATION_STATE_DISABLED.title;
    default:
      return status;
  }
}
